import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { contactsApi } from '@/utils/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { Users, Plus, Loader2, AlertTriangle, Trash2 } from 'lucide-react';
import { relTime } from '@/utils/time';

// ─── Add Person Dialog ──────────────────────────────────────────────────────
function AddPersonDialog({ open, onClose, onAdded }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '', company: '', notes: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setLoading(true);
    try {
      const res = await contactsApi.create(form);
      toast.success('Contact added!');
      onAdded(res.data);
      onClose();
      setForm({ name: '', email: '', phone: '', company: '', notes: '' });
    } catch {
      toast.error('Failed to add contact');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent style={{ background: '#ffffff' }} className="max-w-md border-border">
        <DialogHeader>
          <DialogTitle className="text-base text-foreground">Add Contact</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3 py-1">
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Name <span className="text-destructive">*</span></Label>
            <input
              value={form.name}
              onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
              placeholder="Full name"
              required
              className="w-full h-9 px-3 text-sm rounded-lg border border-border bg-white outline-none focus:border-primary/50"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Email</Label>
              <input
                type="email"
                value={form.email}
                onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                placeholder="email@example.com"
                className="w-full h-9 px-3 text-sm rounded-lg border border-border bg-white outline-none focus:border-primary/50"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Phone</Label>
              <input
                value={form.phone}
                onChange={e => setForm(p => ({ ...p, phone: e.target.value }))}
                placeholder="555-0123"
                className="w-full h-9 px-3 text-sm rounded-lg border border-border bg-white outline-none focus:border-primary/50"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Company</Label>
            <input
              value={form.company}
              onChange={e => setForm(p => ({ ...p, company: e.target.value }))}
              placeholder="Company name"
              className="w-full h-9 px-3 text-sm rounded-lg border border-border bg-white outline-none focus:border-primary/50"
            />
          </div>
          <DialogFooter className="pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm rounded-lg border border-border text-foreground hover:bg-secondary transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !form.name.trim()}
              className="px-4 py-2 text-sm rounded-lg text-white font-medium transition-colors disabled:opacity-50"
              style={{ background: '#6994c6' }}
            >
              {loading ? <><Loader2 size={13} className="inline mr-1 animate-spin" />Adding…</> : 'Add Contact'}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function ContactsPage() {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('everyone'); // 'everyone' | 'met' | 'followup'
  const [addOpen, setAddOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await contactsApi.delete(deleteTarget.id);
      setContacts(prev => prev.filter(c => c.id !== deleteTarget.id));
      toast.success(`Removed ${deleteTarget.name}`);
      setDeleteTarget(null);
    } catch {
      toast.error('Failed to delete contact');
    } finally {
      setDeleting(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const load = useCallback(async () => {
    try {
      const params = {};
      if (search) params.search = search;
      const res = await contactsApi.list(params);
      setContacts(res.data.contacts || []);
    } catch {
      toast.error('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    const timer = setTimeout(load, 250);
    return () => clearTimeout(timer);
  }, [load]);

  const needsCount = contacts.filter(c => c.needs_follow_up).length;
  const metCount = contacts.filter(c => (c.action_card_ids?.length || 0) > 0).length;
  const filtered =
    filter === 'met' ? contacts.filter(c => (c.action_card_ids?.length || 0) > 0)
    : filter === 'followup' ? contacts.filter(c => c.needs_follow_up)
    : contacts;

  const initialsOf = (name) => (name || '?').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  const avColor = (name) => ['blue', 'green', 'butter', 'clay'][(name || '').length % 4];

  return (
    <div className="pagewrap" data-testid="contacts-page">
      {/* Page header */}
      <div className="page-head">
        <div>
          <h1>Contacts</h1>
          <p className="sub"><b>{contacts.length}</b> {contacts.length === 1 ? 'person' : 'people'} Milk has met in your conversations</p>
        </div>
        <button className="btn-capture-top" onClick={() => setAddOpen(true)} data-testid="contacts-add-button">
          <Plus size={16} />Add contact
        </button>
      </div>

      {/* Toolbar */}
      <div className="toolbar">
        <div className="tool-search" style={{ maxWidth: 'none' }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></svg>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search contacts by name, email or company"
            data-testid="contacts-search-input" className="w-full border-0 bg-transparent outline-none text-sm" />
        </div>
        <div className="seg" data-testid="contacts-filter-seg">
          <button className={filter === 'everyone' ? 'on' : ''} onClick={() => setFilter('everyone')} data-testid="contacts-filter-everyone">
            Everyone <span className="n">{contacts.length}</span>
          </button>
          <button className={filter === 'met' ? 'on' : ''} onClick={() => setFilter('met')} data-testid="contacts-filter-met">
            Contacts I met <span className="n">{metCount}</span>
          </button>
          <button className={`fu${filter === 'followup' ? ' on' : ''}`} onClick={() => setFilter('followup')} data-testid="contacts-filter-followup">
            Needs follow-up {needsCount > 0 && <span className="n" data-testid="contacts-followup-count">{needsCount}</span>}
          </button>
        </div>
      </div>

      {/* List */}
      <div className="list-wrap" data-testid="contacts-list">
        {loading ? (
          <div>{[1, 2, 3, 4, 5].map(i => <Skeleton key={i} className="h-[44px] w-full rounded-none" />)}</div>
        ) : filtered.length === 0 ? (
          <div className="ac-empty" data-testid="contacts-empty">
            <div className="eic"><Users size={21} /></div>
            <div className="et">
              {search ? 'No contacts match your search'
                : filter === 'met' ? 'No contacts from meetings yet'
                : filter === 'followup' ? "You're all caught up"
                : 'No contacts yet'}
            </div>
            <div className="es">
              {filter === 'followup' ? 'Nobody needs a follow-up right now.' : 'Contacts you meet in conversations appear here.'}
            </div>
          </div>
        ) : (
          <>
            <div className="cols">
              <div className="c-who">Contact</div>
              <div className="c-co">Company</div>
              <div className="c-met">Last met</div>
              <div className="c-open">Open</div>
              <div className="c-fu">Follow-up</div>
              <div className="ct-del" />
            </div>
            {filtered.map(contact => (
              <div key={contact.id} className="row" data-testid="contact-row" onClick={() => navigate(`/contacts/${contact.id}`)}>
                <div className="c-who">
                  <div className={`av ${avColor(contact.name)}`}>{initialsOf(contact.name)}</div>
                  <div className="who-txt">
                    <div className="tt">{contact.name}</div>
                    <div className="sub2">{contact.email || contact.phone || ''}</div>
                  </div>
                </div>
                <div className="c-co">{contact.company || '—'}</div>
                <div className="c-met" data-testid="contact-row-last-met">{relTime(contact.last_met)}</div>
                <div className="c-open">
                  {contact.open_task_count > 0
                    ? <span className={`badge${contact.overdue_task_count > 0 ? ' over' : ''}`} data-testid="contact-row-open-tasks"
                        title={contact.overdue_task_count > 0 ? `${contact.overdue_task_count} overdue` : undefined}>{contact.open_task_count}</span>
                    : <span className="none">—</span>}
                </div>
                <div className="c-fu">
                  {contact.needs_follow_up
                    ? <span className="ct-fu-pill" data-testid="contact-row-followup-pill"
                        title={contact.follow_up_reason === 'overdue' ? 'Has overdue tasks' : 'No contact in 30+ days'}>
                        <AlertTriangle />{contact.follow_up_reason === 'overdue' ? 'Overdue' : 'Follow up'}</span>
                    : <span className="none">—</span>}
                </div>
                <div className="ct-del" onClick={e => e.stopPropagation()}>
                  <button className="icbtn" onClick={() => setDeleteTarget(contact)} data-testid={`contact-delete-${contact.id}`} title={`Delete ${contact.name}`}>
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      <AddPersonDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onAdded={(contact) => setContacts(prev => [contact, ...prev])}
      />

      <Dialog open={!!deleteTarget} onOpenChange={(o) => { if (!deleting && !o) setDeleteTarget(null); }}>
        <DialogContent style={{ background: '#ffffff' }} className="max-w-md border-border" data-testid="contact-delete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ color: '#B4332E' }}>
              <AlertTriangle size={18} />Delete contact
            </DialogTitle>
            <DialogDescription>
              Remove <strong>{deleteTarget?.name}</strong> from your contacts? Their Action Cards stay, but they'll no longer appear here. This can't be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="pt-2">
            <button type="button" onClick={() => setDeleteTarget(null)} disabled={deleting} data-testid="contact-delete-cancel"
              className="px-4 py-2 text-sm rounded-lg border border-border text-foreground hover:bg-secondary transition-colors disabled:opacity-60">
              Cancel
            </button>
            <button type="button" onClick={confirmDelete} disabled={deleting} data-testid="contact-delete-confirm"
              className="px-4 py-2 text-sm rounded-lg text-white font-medium transition-colors disabled:opacity-60 inline-flex items-center gap-1.5"
              style={{ background: '#B4332E' }}>
              {deleting ? <><Loader2 size={13} className="animate-spin" />Deleting…</> : <><Trash2 size={13} />Delete</>}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
